import { useState } from 'react';
import { Mail, Phone, MapPin, Check } from 'lucide-react';

const revenueRanges = [
  'Under $1M',
  '$1M - $3M',
  '$3M - $5M',
  '$5M - $8M',
  '$8M - $15M',
  '$15M+',
];

const challenges = [
  'Revenue Stagnation or Decline',
  'Pre-Need Sales Development',
  'Operational Modernization',
  'Regulatory Compliance',
  'Board Governance Issues',
  'Exit or Transition Planning',
  'Technology Implementation',
  'Other',
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    revenueRange: '',
    challenge: '',
    phone: '',
    email: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const [widgetLoaded, setWidgetLoaded] = useState(false);

  const handleLoadWidget = () => {
    // Load the embed script
    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.type = 'text/javascript';
    document.body.appendChild(script);
    setWidgetLoaded(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  if (submitted) {
    return (
      <div>
        {/* Hero */}
        <section className="bg-slate-900 text-white py-12 md:py-16">
          <div className="container-custom">
            <div className="max-w-3xl">
              <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4">
                Contact
              </h2>
              <p className="text-lg text-gray-300">
                Get in touch for experienced fractional COO leadership.
              </p>
            </div>
          </div>
        </section>

        {/* Success Message */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-serif text-xl font-medium text-slate-900 mb-3">
                Thank You for Your Interest
              </h2>
              <p className="text-slate-600 mb-6">
                Your message has been received. Todd Sexton will review your information 
                and respond within 2 business days to schedule a confidential consultation.
              </p>
              <p className="text-slate-500 text-sm">
                If your matter is urgent, please call directly at (555) 123-4567.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4">
              Contact
            </h2>
            <p className="text-lg text-gray-300">
              Let's discuss your organization's next phase.
            </p>
          </div>
        </div>
      </section>
      {/* Contact Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 max-w-content mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-5">
              <h2 className="font-serif text-xl font-medium text-slate-900 mb-4">
                Let's Discuss Your Organization's Next Phase
              </h2>
              <p className="text-slate-600 mb-6 text-sm leading-relaxed">A fast, no-pressure conversation to see whether our expertise matches your organization’s current needs — and to answer any questions about how these engagements work.</p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-slate-900 mb-0.5 text-sm">Phone</h3>
                    <a href="tel:844-622-9007" className="text-slate-600 text-sm hover:text-accent-600 transition-colors">(844) 622-9007</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-slate-900 mb-0.5 text-sm">Email</h3>
                    <p className="text-slate-600 text-sm">info@blackswan-consulting.com</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-slate-900 mb-0.5 text-sm">Location</h3>
                    <p className="text-slate-600 text-sm">Serving deathcare organizations nationwide</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 border border-gray-100">
                <h3 className="font-medium text-slate-900 mb-1 text-sm">Response Commitment</h3>
                <p className="text-slate-600 text-xs">
                  All inquiries are treated with strict confidentiality. Expect a response 
                  within 2 business days.
                </p>
              </div>
            </div>

            {/* Booking Widget */}
            <div className="lg:col-span-7">
              {!widgetLoaded ? (
                <div className="flex items-center justify-center bg-gray-50 border border-gray-200 rounded-lg" style={{height: '700px'}}>
                  <div className="text-center px-6">
                    <div className="w-12 h-12 bg-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <h3 className="font-serif text-lg font-medium text-slate-900 mb-2">Ready to Schedule?</h3>
                    <p className="text-slate-500 text-sm mb-4">Click below to load the booking calendar and find a time that works for you.</p>
                    <button
                      onClick={handleLoadWidget}
                      className="inline-flex items-center gap-2 bg-accent-600 text-white px-5 py-2.5 rounded-sm font-medium text-sm hover:bg-accent-700 transition-colors"
                    >
                      Load Booking Calendar
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                    </button>
                  </div>
                </div>
              ) : (
                <iframe
                  src="https://api.leadconnectorhq.com/widget/booking/AsE5KoUWHvxJrhRkHNQw"
                  allow="payment"
                  style={{width: '100%', border: 'none', overflow: 'hidden', height: '700px'}}
                  scrolling="no"
                  id="AsE5KoUWHvxJrhRkHNQw_1787580871392"
                  title="Schedule a consultation"
                ></iframe>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
